/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Store, Code, Landmark, Users, CheckCircle, Shield } from 'lucide-react';

export const UsersDoc = () => (
  <div className="space-y-12 animate-fade-in">
    <section>
        <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-medium">
            GhostPay is designed for a diverse ecosystem of sovereign participants. From global merchants to autonomous AI agents, our platform provides tailored interfaces and tools for every stakeholder.
        </p>
    </section>

    <div className="grid md:grid-cols-2 gap-6">
        <div className="p-6 bg-white dark:bg-white/[0.02] rounded-2xl border border-stone-200 dark:border-white/10 group hover:border-nobel-gold/50 transition-colors">
            <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-stone-100 dark:bg-white/10 rounded-xl flex items-center justify-center text-stone-900 dark:text-white">
                    <Store size={24} />
                </div>
                <h3 className="font-serif text-xl font-bold text-stone-900 dark:text-white">Merchants</h3>
            </div>
            <p className="text-sm text-stone-500 dark:text-stone-400 mb-6 leading-relaxed">
                Businesses selling goods or services, both online and physically. They require robust POS systems, inventory tracking, and tax compliance tools.
            </p>
            <ul className="space-y-3">
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>GhostPOS:</strong> Instant retail checkout terminals.</span>
                </li>
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>Inventory:</strong> On-chain stock management.</span>
                </li>
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>Analytics:</strong> Revenue & customer insights.</span>
                </li>
            </ul>
        </div>

        <div className="p-6 bg-white dark:bg-white/[0.02] rounded-2xl border border-stone-200 dark:border-white/10 group hover:border-nobel-gold/50 transition-colors">
            <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-stone-100 dark:bg-white/10 rounded-xl flex items-center justify-center text-stone-900 dark:text-white">
                    <Code size={24} />
                </div>
                <h3 className="font-serif text-xl font-bold text-stone-900 dark:text-white">Developers & Bots</h3>
            </div>
            <p className="text-sm text-stone-500 dark:text-stone-400 mb-6 leading-relaxed">
                Engineers building custom integrations and autonomous AI agents. They utilize the x402 protocol for programmatic payments and data access.
            </p>
            <ul className="space-y-3">
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>x402 Gateway:</strong> Machine-to-machine payments.</span>
                </li>
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>SDKs:</strong> JS/TS & Python libraries.</span>
                </li>
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>Webhooks:</strong> Real-time event notifications.</span>
                </li>
            </ul>
        </div>

        <div className="p-6 bg-white dark:bg-white/[0.02] rounded-2xl border border-stone-200 dark:border-white/10 group hover:border-nobel-gold/50 transition-colors">
            <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-stone-100 dark:bg-white/10 rounded-xl flex items-center justify-center text-stone-900 dark:text-white">
                    <Landmark size={24} />
                </div>
                <h3 className="font-serif text-xl font-bold text-stone-900 dark:text-white">Institutions</h3>
            </div>
            <p className="text-sm text-stone-500 dark:text-stone-400 mb-6 leading-relaxed">
                Organizations managing significant treasury assets. They need multi-sig security, detailed audit logs, and permissioned role management.
            </p>
            <ul className="space-y-3">
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>Multi-Sig:</strong> Shared custody vaults.</span>
                </li>
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>Roles:</strong> Granular access control (RBAC).</span>
                </li>
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>Compliance:</strong> Exportable audit trails.</span>
                </li>
            </ul>
        </div>

        <div className="p-6 bg-white dark:bg-white/[0.02] rounded-2xl border border-stone-200 dark:border-white/10 group hover:border-nobel-gold/50 transition-colors">
            <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-stone-100 dark:bg-white/10 rounded-xl flex items-center justify-center text-stone-900 dark:text-white">
                    <Users size={24} />
                </div>
                <h3 className="font-serif text-xl font-bold text-stone-900 dark:text-white">Consumers</h3>
            </div>
            <p className="text-sm text-stone-500 dark:text-stone-400 mb-6 leading-relaxed">
                Individuals using the GhostWallet mobile app for daily spending, p2p transfers, and asset management.
            </p>
            <ul className="space-y-3">
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>GhostWallet:</strong> Secure mobile interface.</span>
                </li>
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>Privacy:</strong> Stealth address generation.</span>
                </li>
                <li className="flex items-start gap-3 text-xs text-stone-600 dark:text-stone-300">
                    <CheckCircle size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                    <span><strong>Rewards:</strong> Tokenized loyalty programs.</span>
                </li>
            </ul>
        </div>
    </div>
  </div>
);